import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { dateFr, money, computeTotals } from "@/lib/format";
import LineItemsEditor from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Play, PencilSimple, Trash, ArrowsClockwise } from "@phosphor-icons/react";

const FREQ = { daily: "Quotidienne", weekly: "Hebdomadaire", monthly: "Mensuelle", yearly: "Annuelle" };
const empty = { client_id: "", frequency: "monthly", start_date: "", end_date: "", notes: "", auto_send: true, active: true };
const newItems = () => [{ description: "", quantity: 1, unit_price: 0, taxable: true }];

export default function Recurrences() {
  const [recs, setRecs] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [items, setItems] = useState(newItems());
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/recurrences").then((r) => setRecs(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(empty); setItems(newItems()); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ client_id: r.client_id, frequency: r.frequency, start_date: r.next_run_date, end_date: r.end_date || "", notes: r.notes || "", auto_send: r.auto_send, active: r.active });
    setItems(r.line_items.map((i) => ({ ...i }))); setEditId(r.id); setOpen(true);
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Sélectionnez un client."); return; }
    if (!items.some((i) => i.description)) { toast.error("Ajoutez au moins une ligne."); return; }
    const payload = {
      client_id: form.client_id, frequency: form.frequency, start_date: form.start_date || null, end_date: form.end_date || null,
      notes: form.notes, auto_send: form.auto_send, active: form.active,
      line_items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, taxable: i.taxable !== false })),
    };
    try {
      if (editId) await api.put(`/recurrences/${editId}`, payload);
      else await api.post("/recurrences", payload);
      toast.success(editId ? "Récurrence mise à jour" : "Récurrence créée"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runNow = async (r) => {
    toast.loading("Génération…", { id: "run" });
    try { const { data } = await api.post(`/recurrences/${r.id}/run-now`); toast.success(`Facture ${data.invoice_number} générée${data.sent ? " et envoyée" : ""}`, { id: "run" }); load(); }
    catch (e) { toast.error(apiError(e), { id: "run" }); }
  };
  const del = async () => { try { await api.delete(`/recurrences/${delId}`); toast.success("Récurrence supprimée"); setDelId(null); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-6" data-testid="recurrences-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Récurrences</h1>
          <p className="text-muted-foreground mt-1">Factures automatiques générées selon un calendrier.</p>
        </div>
        <Button data-testid="new-recurrence-btn" className="rounded-sm" onClick={openNew}><Plus size={18} className="mr-1" /> Nouvelle récurrence</Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Client</TableHead>
              <TableHead>Fréquence</TableHead>
              <TableHead>Prochaine émission</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Envoi auto</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucune récurrence configurée.</TableCell></TableRow>}
            {recs.map((r) => {
              const t = computeTotals(r.line_items);
              return (
                <TableRow key={r.id} data-testid={`recurrence-row-${r.id}`}>
                  <TableCell className="font-medium">{r.client_name}</TableCell>
                  <TableCell>{FREQ[r.frequency]}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{dateFr(r.next_run_date)}{r.end_date && <span className="block text-xs">jusqu'au {dateFr(r.end_date)}{r.contract_id ? " · contrat" : ""}</span>}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(t.total)}</TableCell>
                  <TableCell>{r.auto_send ? "Oui" : "Non"}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-sm border ${r.active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-muted text-muted-foreground border-border"}`}>{r.active ? "Active" : "En pause"}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button data-testid={`run-now-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Générer maintenant" onClick={() => runNow(r)}><Play size={16} /></Button>
                      <Button data-testid={`edit-recurrence-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)}><PencilSimple size={16} /></Button>
                      <Button data-testid={`delete-recurrence-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => setDelId(r.id)}><Trash size={16} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ArrowsClockwise size={20} /> {editId ? "Modifier la récurrence" : "Nouvelle récurrence"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="rec-client-select" className="rounded-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fréquence</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger data-testid="rec-frequency-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(FREQ).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Première émission</Label>
                <Input data-testid="rec-start-date" type="date" className="rounded-sm" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Fin (optionnel)</Label>
                <Input data-testid="rec-end-date" type="date" className="rounded-sm" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Lignes</Label>
              <LineItemsEditor items={items} setItems={setItems} />
            </div>

            <Textarea data-testid="rec-notes" className="rounded-sm" placeholder="Notes (optionnel)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Switch data-testid="rec-autosend" checked={form.auto_send} onCheckedChange={(v) => setForm({ ...form, auto_send: v })} />
                Envoyer automatiquement par courriel
              </label>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Switch data-testid="rec-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-recurrence-btn" className="rounded-sm" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Supprimer cette récurrence ?</AlertDialogTitle><AlertDialogDescription>Les factures déjà générées seront conservées.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-recurrence" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
